import React, { useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { Toasts, useModalContext } from '@fourthwall/components'
import { useVerifyDomainMutation } from '@modules/Dashboard/pages/Settings/pages/Domain/queries/useVerifyDomainMutation'
import { routing } from '@utils/routing'

import { DomainConfigurationView } from './DomainConfigurationView'
import type { FormValues as EmailSendFormValues } from './components/EmailSendModal/EmailSendModal.types'
import { useDnsConfiguration } from './hooks/useDnsConfiguration'
import { useSendEntriesViaEmailMutation } from './queries/useSendEntriesViaEmailMutation'

export const DomainConfigurationContainer = () => {
  const {
    domain,
    provider,
    mode,
    domainDnsRecords,
    warning,
    isLoading,
    isPaidDomain,
    isAutomaticSetupInProgress,
    invalidateDnsQuery,
    verifyProviderDetails,
    setUpAutomatically,
  } = useDnsConfiguration()
  const { sendEntriesViaEmailMutation } = useSendEntriesViaEmailMutation()
  const { verifyDomainMutation } = useVerifyDomainMutation()
  const { close } = useModalContext()
  const navigate = useNavigate()

  const onFocus = useCallback(() => {
    verifyDomainMutation.mutate([])
    verifyProviderDetails?.()
  }, [verifyDomainMutation, verifyProviderDetails])

  useEffect(() => {
    if (!isLoading) {
      window.addEventListener('focus', onFocus)

      return () => {
        window.removeEventListener('focus', onFocus)
      }
    }
  }, [isLoading, onFocus])

  const handleSend = ({ email }: EmailSendFormValues) => {
    sendEntriesViaEmailMutation.mutate(email, {
      onSuccess: close,
    })
  }

  const handleDomainVerification = () => {
    verifyDomainMutation.mutate([], {
      onSuccess: (data) => {
        invalidateDnsQuery()

        if (data.dnsList.areAllEntriesComplete) {
          navigate(routing.settings.domain.self)
          Toasts.notify(`${data.dnsList.domain} was added and is currently syncing.`, {
            type: 'info',
          })
        }
      },
      onError: () => {
        Toasts.notify('Failed to verify domain', { type: 'error' })
      },
    })
  }

  return (
    <DomainConfigurationView
      domain={domain}
      provider={provider}
      mode={mode}
      domainDnsRecords={domainDnsRecords}
      isLoading={isLoading}
      isPaidDomain={isPaidDomain}
      isVerificationInProgress={verifyDomainMutation.isLoading}
      isAutomaticSetupInProgress={isAutomaticSetupInProgress}
      warning={warning}
      onSend={handleSend}
      onDomainVerification={handleDomainVerification}
      onAutomaticSetup={setUpAutomatically}
    />
  )
}
